import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Zap, ArrowRight } from 'lucide-react';

const PULSE_BANNER_URL = 'https://assets.orbis.build/dashboard/pulse_banner.png';

/**
 * Mini orbital visualization — Orbis mark at center with project dots orbiting.
 * Inspired by radial-orbital-timeline (21st.dev).
 * Pure CSS animation, $0 cost.
 */
function OrbitalVisualization({ projects }) {
  const deployed = projects.filter(p => p.status === 'deployed' || p.custom_domain).length;
  const inProgress = projects.filter(p => p.magic_mode && p.magic_state?.current_stage && !['deploy', 'review'].includes(p.magic_state?.current_stage)).length;
  const needsAction = projects.filter(p => p.magic_mode && p.magic_state?.current_stage === 'assets').length;

  // Up to 3 orbiting dots
  const dots = [];
  if (deployed > 0) dots.push({ color: 'var(--orbis-positive)', count: deployed, label: 'live' });
  if (inProgress > 0) dots.push({ color: 'var(--orbis-blue)', count: inProgress, label: 'building' });
  if (needsAction > 0) dots.push({ color: 'var(--orbis-amber)', count: needsAction, label: 'attention' });
  if (dots.length === 0) dots.push({ color: 'var(--orbis-t4)', count: 0, label: 'empty' });

  const uid = useMemo(() => `orbit-${Math.random().toString(36).slice(2, 6)}`, []);

  return (
    <div style={{ width: 72, height: 72, position: 'relative', flexShrink: 0 }}>
      {/* Center orb */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 20, height: 20, borderRadius: '50%',
        background: 'radial-gradient(circle, var(--orbis-blue) 0%, var(--orbis-green) 100%)',
        boxShadow: '0 0 20px rgba(184, 204, 223, 0.3)',
      }}>
        {/* Ping rings */}
        <div className={`${uid}-ping`} style={{
          position: 'absolute', inset: -6, borderRadius: '50%',
          border: '1px solid var(--orbis-blue)',
          opacity: 0,
        }} />
      </div>

      {/* Orbit ring */}
      <div style={{
        position: 'absolute', inset: 8, borderRadius: '50%',
        border: '0.5px solid rgba(184, 204, 223, 0.08)',
      }} />

      {/* Orbiting dots */}
      {dots.map((dot, i) => (
        <div
          key={i}
          className={`${uid}-orbit-${i}`}
          style={{
            position: 'absolute', top: '50%', left: '50%',
            width: 0, height: 0,
          }}
        >
          <div style={{
            position: 'absolute',
            width: 8, height: 8, borderRadius: '50%',
            background: dot.color,
            boxShadow: `0 0 8px ${dot.color}60`,
            transform: 'translate(-4px, -4px)',
            top: -28,
          }} />
        </div>
      ))}

      <style>{`
        .${uid}-ping {
          animation: ${uid}-ping-anim 3s ease-out infinite;
        }
        @keyframes ${uid}-ping-anim {
          0% { transform: scale(1); opacity: 0.4; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        ${dots.map((_, i) => `
          .${uid}-orbit-${i} {
            animation: ${uid}-spin ${12 + i * 5}s linear infinite;
            animation-delay: ${i * -4}s;
          }
        `).join('')}
        @keyframes ${uid}-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * Orbis Pulse — AI narrative card synthesizing project data.
 * $0 cost: all intelligence computed client-side.
 */
export default function OrbisPulse({ projects, credits, onNavigateToProject }) {
  const prefersReducedMotion = useReducedMotion();
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const { narrative, priority, priorityProject } = useMemo(() => {
    const total = projects.length;
    const deployed = projects.filter(p => p.status === 'deployed' || p.custom_domain).length;
    const needsWork = projects.filter(p =>
      p.magic_mode && p.magic_state?.current_stage &&
      !['deploy', 'review'].includes(p.magic_state?.current_stage)
    );

    // Build narrative
    const parts = [];
    if (total > 0) {
      parts.push(`Your portfolio has ${total} project${total !== 1 ? 's' : ''}`);
      const statusParts = [];
      if (deployed > 0) statusParts.push(`${deployed} live`);
      if (needsWork.length > 0) statusParts.push(`${needsWork.length} need${needsWork.length === 1 ? 's' : ''} attention`);
      if (statusParts.length > 0) parts[0] += ` \u2014 ${statusParts.join(', ')}`;
      parts[0] += '.';
    } else {
      parts.push('Welcome to Orbis. Create your first project to get started.');
    }

    // Credits usage
    if (credits?.monthlyCredits > 0) {
      const pct = Math.round((credits.lifetimeUsed / credits.monthlyCredits) * 100);
      parts.push(`You've used ${credits.balance} of ${credits.monthlyCredits} credits this month.`);
    }

    // Priority project
    const sorted = [...needsWork].sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at));
    const priorityProject = sorted[0] || null;
    let priority = null;
    if (priorityProject) {
      const stage = priorityProject.magic_state?.current_stage || 'brief';
      const stageLabels = { brief: 'Brief', design_system: 'Design System', structure: 'Structure', assets: 'Assets', build: 'Build', review: 'Review' };
      priority = `Complete the ${stageLabels[stage] || stage} stage for "${priorityProject.name}" to move forward.`;
    }

    return { narrative: parts.join(' '), priority, priorityProject };
  }, [projects, credits]);

  const uid = useMemo(() => `pulse-${Math.random().toString(36).slice(2, 6)}`, []);

  return (
    <motion.div
      initial={prefersReducedMotion ? {} : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.5 }}
      style={{
        position: 'relative',
        borderRadius: 16,
        padding: 2,
        marginBottom: 20,
        overflow: 'hidden',
      }}
    >
      {/* Animated gradient border */}
      <div className={`${uid}-border`} style={{
        position: 'absolute', inset: 0, borderRadius: 16, zIndex: 0,
        background: 'linear-gradient(135deg, var(--orbis-blue), var(--orbis-amber), var(--orbis-green), var(--orbis-blue))',
        backgroundSize: '300% 300%',
        opacity: 0.4,
      }} />

      {/* Inner card */}
      <div style={{
        position: 'relative', zIndex: 1,
        background: 'var(--orbis-s1)',
        borderRadius: 14,
        padding: '16px 22px',
        overflow: 'hidden',
        minHeight: 140,
      }}>
        {/* Content layer — above image */}
        <div className="flex items-center gap-6" style={{ position: 'relative', zIndex: 2, minHeight: 108 }}>
          {/* Orbital visualization */}
          <OrbitalVisualization projects={projects} />

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Header */}
            <div style={{ marginBottom: 12 }}>
              <div className="flex items-center gap-2">
                <Zap className="w-3.5 h-3.5" style={{ color: 'var(--orbis-blue)' }} />
                <span style={{
                  fontSize: 11, fontFamily: 'var(--orbis-mono)', fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--orbis-t3)',
                }}>
                  Orbis Pulse
                </span>
              </div>
              <span className={`${uid}-refresh`} style={{
                fontSize: 10, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)',
                marginTop: 2, display: 'block', paddingLeft: 22,
              }}>
                Refreshed just now
              </span>
            </div>

            {/* Narrative */}
            <p style={{
              fontSize: 14, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)',
              lineHeight: 1.6, marginBottom: priority ? 12 : 0,
            }}>
              {narrative}
            </p>

            {/* Priority action */}
            {priority && priorityProject && (
              <div className="flex items-center justify-between">
                <p style={{ fontSize: 13, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>
                  {priority}
                </p>
                <button
                  onClick={() => onNavigateToProject(priorityProject)}
                  className="flex items-center gap-1 cursor-pointer hover:gap-2 transition-all shrink-0"
                  style={{
                    background: 'none', border: 'none', padding: '4px 0',
                    fontSize: 12, fontWeight: 500, color: 'var(--orbis-blue)',
                    fontFamily: 'var(--orbis-font)', marginLeft: 16,
                  }}
                >
                  Continue <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Decorative banner image — right side, behind content */}
        {!imgError && (
          <div
            className={`hidden md:block ${uid}-pulse-img`}
            style={{
              position: 'absolute',
              top: 0, right: 0, bottom: 0,
              width: '45%', maxWidth: 300,
              zIndex: 1,
              pointerEvents: 'none',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.15) 35%, rgba(0,0,0,0.8) 70%, rgba(0,0,0,1) 85%)',
              maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.15) 35%, rgba(0,0,0,0.8) 70%, rgba(0,0,0,1) 85%)',
            }}
          >
            <img
              src={PULSE_BANNER_URL}
              alt=""
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover', objectPosition: 'center right',
                opacity: imgLoaded ? 0.45 : 0,
                transition: 'opacity 0.8s ease',
              }}
            />
          </div>
        )}
      </div>

      <style>{`
        .${uid}-border {
          animation: ${uid}-gradient 10s ease infinite;
        }
        @keyframes ${uid}-gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .${uid}-refresh {
          animation: ${uid}-pulse 4s ease-in-out infinite;
        }
        @keyframes ${uid}-pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        ${prefersReducedMotion ? '' : `
        .${uid}-pulse-img {
          animation: ${uid}-float 20s ease-in-out infinite;
        }
        @keyframes ${uid}-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        `}
      `}</style>
    </motion.div>
  );
}
